import {
  ChangeEvent,
  useCallback,
  useEffect,
  useState,
  useRef,
  ReactNode,
} from "react";
import classnames from "classnames";
import "./MultiRangeSlider.scss";
import { parseEventFloat } from "../../util";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "reactstrap";
import { SliderGradations } from "./SliderGradations";
import { ReferenceMarkers } from "./ReferenceMarker";
import { ErrorDisplay } from "./ErrorDisplay";
import { Unit } from "../../models/range.model";

interface Props {
  readonly min: number;
  readonly max: number;
  readonly step: number;
  readonly type: "single" | "double";
  readonly initialMin: number;
  readonly initialMax?: number;
  readonly onChange?: (min: number, max: number, id?: string) => void;
  readonly format?: (value: number) => string;
  readonly formatMinMax?: (value: number) => string;
  readonly title?: string;
  readonly toolTip?: ReactNode;
  readonly id?: string;
  readonly referenceMarkers?: { value: number; key: string; unit: Unit }[];
  readonly direction?: "rtl" | "ltr";
  readonly showRangeMin?: boolean;
  readonly showRangeMax?: boolean;
  hasNumericInput?: boolean;
  readonly numericInputStep?: number;
  readonly forwardTransform?: (value: number) => number;
  readonly reverseTransform?: (value: number) => number;
  readonly unit?: string;
}

// Derived from https://dev.to/sandra_lewis/building-a-multi-range-slider-in-react-from-scratch-4dl1
export const MultiRangeSlider = ({
  min,
  max,
  step: sliderStep,
  type,
  initialMin,
  initialMax,
  onChange,
  format,
  formatMinMax,
  title,
  toolTip,
  id,
  referenceMarkers: argReferenceMarkers = [],
  direction = "ltr",
  showRangeMin = true,
  showRangeMax = true,
  hasNumericInput = true,
  numericInputStep = 1,
  forwardTransform,
  reverseTransform,
  unit,
}: Props) => {
  const [minState, setMinState] = useState(Math.max(initialMin, min));
  const [maxState, setMaxState] = useState(
    initialMax !== undefined ? Math.min(initialMax, max) : max
  );
  const [toolTipOpen, setToolTipOpen] = useState(false);

  const toggleToolTip = () => setToolTipOpen(!toolTipOpen);

  hasNumericInput &&= !!forwardTransform && !!reverseTransform;

  const initialNumericInputState = reverseTransform?.(
    Math.max(initialMin, min)
  );

  const [minNumericState, setMinNumericState] = useState<number | undefined>(
    initialNumericInputState
  );

  const numericInputRef = useRef<HTMLInputElement>(null);
  const minValRef = useRef<HTMLInputElement>(null);
  const maxValRef = useRef<HTMLInputElement>(null);
  const range = useRef<HTMLDivElement>(null);
  const sliderParentRef = useRef<HTMLDivElement>(null);

  const referenceMarkers = argReferenceMarkers.filter(
    ({ value }) => min <= value && value <= max
  );

  if (forwardTransform)
    sliderStep = Math.min(sliderStep, forwardTransform(numericInputStep));

  const isDouble = type === "double";

  const getPercentFromLeft = useCallback(
    (value: number) =>
      direction === "ltr"
        ? Math.round(((value - min) / (max - min)) * 100)
        : 100 - Math.round(((value - min) / (max - min)) * 100),
    [direction, min, max]
  );

  useEffect(() => {
    if (maxValRef.current && minValRef.current && isDouble) {
      const pcs = [getPercentFromLeft(minState), getPercentFromLeft(maxState)];

      if (range.current) {
        range.current.style.left = `${Math.min(...pcs)}%`;
        range.current.style.width = `${Math.abs(pcs[0] - pcs[1])}%`;
      }
    }
  }, [minState, maxState, getPercentFromLeft, isDouble]);

  useEffect(() => {
    if (!reverseTransform) return;
    setMinNumericState(reverseTransform(minState));
    if (numericInputRef.current)
      numericInputRef.current.className = classnames(
        "form-control",
        "is-valid"
      );
  }, [reverseTransform, minState]);

  // Get min and max values when their state changes
  useEffect(() => {
    onChange?.(minState, isDouble ? maxState : minState, id);
  }, [minState, maxState, onChange, isDouble, id]);

  const maybeFormatMinMax = (value: number) =>
    formatMinMax ? formatMinMax(value) : value;

  const extremeLeft = maybeFormatMinMax(direction === "ltr" ? min : max);
  const extremeRight = maybeFormatMinMax(direction === "ltr" ? max : min);

  const numericInputOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    let value = parseEventFloat(e);
    setMinNumericState(value);
    if (value === undefined) {
      e.target.className = classnames("form-control", "is-invalid");
      return;
    }
    if (forwardTransform) value = forwardTransform(value);
    if (value > max || value < min) {
      e.target.className = classnames("form-control", "is-invalid");
      return;
    }
    e.target.className = classnames("form-control", "is-valid");
    setMinState(value);
  };
  return (
    <div className="sliderparent mb-4" ref={sliderParentRef}>
      <div className="slidertoptext">
        <div className="slidertitle" id={`${id}-title`}>
          {title}
          {toolTip && (
            <FontAwesomeIcon
              icon={faCircleInfo}
              className="ms-1"
              size="sm"
              color="#ced4da;"
            />
          )}
        </div>
        {toolTip && (
          <Tooltip
            placement="top"
            isOpen={toolTipOpen}
            target={`${id}-title`}
            trigger="hover"
            autohide={false}
            toggle={toggleToolTip}
          >
            {toolTip}
          </Tooltip>
        )}
        {!isDouble && hasNumericInput && (
          <>
            <input
              type="number"
              ref={numericInputRef}
              value={minNumericState ?? ""}
              onChange={numericInputOnChange}
              step={numericInputStep}
              placeholder=""
              className=""
              style={{
                width: "7.5em",
                marginLeft: 5,
                marginTop: 2,
                marginRight: ".5em",
                textAlign: "right",
                paddingRight: "2em",
                height: "2.5em",
              }}
            />
            <ErrorDisplay inputRef={numericInputRef} />
            {unit}
          </>
        )}
      </div>
      <div className="slidercontainer">
        <input
          type="range"
          min={min}
          max={max}
          step={sliderStep}
          value={minState}
          ref={minValRef}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            const value = isDouble
              ? Math.min(+event.target.value, maxState - sliderStep)
              : +event.target.value;
            if (value === minState) return;
            setMinState(value);
            event.target.value = value.toString();
          }}
          // Ensure chrome (bug?) doesn't prevent slider drag when there is a selection
          onMouseDown={() => {
            const sel = window.getSelection();
            sel?.removeAllRanges();
          }}
          className={classnames("thumb thumb--zindex-3", {
            thumb__single: !isDouble,
            thumb__double: isDouble,
            "thumb--zindex-5": minState > max - 100,
          })}
          style={{ direction: direction }}
        />
        {isDouble && (
          <input
            type="range"
            min={min}
            max={max}
            step={sliderStep}
            value={maxState}
            ref={maxValRef}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const value = Math.max(
                +event.target.value,
                minState + sliderStep
              );
              if (value === maxState) return;
              setMaxState(value);
              event.target.value = value.toString();
            }}
            // Ensure chrome (bug?) doesn't prevent slider drag when there is a selection
            onMouseDown={() => {
              const sel = window.getSelection();
              sel?.removeAllRanges();
            }}
            className={classnames("thumb thumb--zindex-4", {
              thumb__single: !isDouble,
              thumb__double: isDouble,
            })}
            style={{ direction: direction }}
          />
        )}
        <div className="slider">
          {isDouble && <div className="slider__track"></div>}
          {isDouble && <div ref={range} className="slider__range"></div>}
          <div className="slider__below-text">
            <div className="d-flex justify-content-between w-100">
              <span>{showRangeMin && extremeLeft}</span>
              <span>{showRangeMax && extremeRight}</span>
            </div>
          </div>

          <SliderGradations min={min} max={max} />

          <ReferenceMarkers
            direction={direction}
            min={min}
            max={max}
            referenceMarkers={referenceMarkers}
          />
        </div>
      </div>
    </div>
  );
};
